import '../../css/custom-datalist.css';

export interface ICustomDatalistItem {
    text: string;
    value: string;
}

const ACTIVE_CLASS = 'active';
const ITEM_CLASS = 'custom-dropdown-item';

/**
 * A custom (non-native) datalist dropdown bound to a text input, with keyboard navigation
 * and ARIA combobox semantics.
 *
 * Re-constructing this against the same container element (e.g. re-rendering search
 * results into the same DOM node) automatically tears down the previous instance's
 * document-level listener first, so callers can safely call `new CustomDatalist(...)`
 * repeatedly without leaking listeners. Call `dispose()` when the widget itself is
 * being removed for good.
 */
export class CustomDatalist {
    private static readonly _activeControllers = new WeakMap<HTMLElement, AbortController>();

    private readonly _container: HTMLElement;
    private readonly _input: HTMLInputElement;
    private readonly _abortController = new AbortController();
    private _items: ICustomDatalistItem[];
    private _shouldDisplayList = true;

    constructor(inputId: string, containerId: string, items: ICustomDatalistItem[]) {
        const container = document.getElementById(containerId);
        const input = document.getElementById(inputId);

        if (!(container instanceof HTMLElement))
            throw new Error(`CustomDatalist: container element "#${containerId}" was not found.`);
        if (!(input instanceof HTMLInputElement))
            throw new Error(`CustomDatalist: input element "#${inputId}" was not found.`);

        this._container = container;
        this._input = input;
        this._items = items;

        CustomDatalist._activeControllers.get(this._container)?.abort();
        CustomDatalist._activeControllers.set(this._container, this._abortController);

        this._setupContainer();
        this._setupInputListeners();
        this._renderList();
        this._filterList(this._input.value);
    }

    /**
     * Removes all listeners this instance attached (including the document-level one).
     * Safe to call more than once.
     */
    public dispose(): void {
        this._abortController.abort();
        if (CustomDatalist._activeControllers.get(this._container) === this._abortController)
            CustomDatalist._activeControllers.delete(this._container);
    }

    /**
     * Replaces the list's items and re-renders it against the current filter.
     */
    public updateDataList(items: ICustomDatalistItem[]): void {
        this._items = items;
        this._renderList();
        this._filterList(this._input.value);
    }

    private _setupContainer(): void {
        this._container.innerHTML = '';
        this._container.setAttribute('role', 'listbox');
        this._container.className = 'custom-dropdown-list';

        const { signal } = this._abortController;

        this._container.addEventListener('click', this._handleItemClick, { signal });
        document.addEventListener('click', this._handleOutsideClick, { signal });
    }

    private _setupInputListeners(): void {
        const { signal } = this._abortController;

        this._input.addEventListener('input', this._handleInput, { signal });
        this._input.addEventListener('click', this._handleInputClick, { signal });
        this._input.addEventListener('keydown', this._handleKeydown, { signal });
    }

    private readonly _handleOutsideClick = (event: MouseEvent): void => {
        const target = event.target as Node | null;
        if (!this._container.contains(target) && !this._input.contains(target))
            this._closeList();
    };

    private readonly _handleInput = (): void => {
        this._shouldDisplayList = true;
        this._filterList(this._input.value);
    };

    private readonly _handleInputClick = (): void => {
        if (this._container.children.length > 0)
            this._container.style.display = 'block';
    };

    private readonly _handleItemClick = (event: MouseEvent): void => {
        const item = (event.target as HTMLElement | null)?.closest<HTMLElement>(`.${ITEM_CLASS}`);
        if (!item) return;

        this._selectItem(item.textContent ?? '');
    };

    private readonly _handleKeydown = (event: KeyboardEvent): void => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this._moveActiveItem(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this._moveActiveItem(-1);
                break;
            case 'Enter':
            case 'Tab':
                this._selectActiveItem();
                break;
        }
    };

    private _getVisibleItems(): HTMLElement[] {
        return Array.from(this._container.children).filter(
            (child): child is HTMLElement => (child as HTMLElement).style.display !== 'none'
        );
    }

    private _moveActiveItem(direction: 1 | -1): void {
        const visibleItems = this._getVisibleItems();
        if (visibleItems.length === 0) return;

        const currentIndex = visibleItems.findIndex(item => item.classList.contains(ACTIVE_CLASS));
        const newIndex = Math.min(Math.max(currentIndex + direction, 0), visibleItems.length - 1);

        if (newIndex === currentIndex) return;

        if (currentIndex !== -1)
            visibleItems[currentIndex].classList.remove(ACTIVE_CLASS);

        const newItem = visibleItems[newIndex];
        newItem.classList.add(ACTIVE_CLASS);
        newItem.scrollIntoView({ block: 'nearest' });
        this._input.setAttribute('aria-activedescendant', newItem.id);
    }

    private _selectActiveItem(): void {
        const activeItem = this._container.querySelector<HTMLElement>(`.${ACTIVE_CLASS}`);
        if (!activeItem) return;

        this._selectItem(activeItem.textContent ?? '');
    }

    private _selectItem(text: string): void {
        this._input.value = text;
        this._input.dispatchEvent(new Event('input', { bubbles: true }));
        this._closeList();
    }

    private _closeList(): void {
        this._shouldDisplayList = false;
        this._container.style.display = 'none';
        this._input.setAttribute('aria-expanded', 'false');
        this._input.removeAttribute('aria-activedescendant');
    }

    private _renderList(): void {
        this._container.innerHTML = '';

        this._items.forEach((item, index) => {
            const listItem = document.createElement('li');
            listItem.id = `${this._container.id}_option_${index}`;
            listItem.className = ITEM_CLASS;
            listItem.textContent = item.text;
            listItem.dataset.value = item.value;
            listItem.setAttribute('role', 'option');
            listItem.setAttribute('aria-selected', 'false');
            this._container.appendChild(listItem);
        });
    }

    private _filterList(filter: string): void {
        const lowerFilter = filter.toLowerCase();

        Array.from(this._container.children).forEach(child => {
            const item = child as HTMLElement;
            const matches = !!item.textContent?.toLowerCase().includes(lowerFilter);
            item.style.display = matches ? 'block' : 'none';
        });

        if (filter === '' || !this._shouldDisplayList) {
            this._container.style.display = 'none';
            this._input.setAttribute('aria-expanded', 'false');
        } else {
            this._container.style.display = 'block';
            this._input.setAttribute('aria-expanded', 'true');
        }
    }
}
